import React, { useState, useRef, useEffect } from 'react';
import { forEach, isEqual, isString, uniqueId } from 'lodash';
import Draggable from 'react-draggable';
import './ResizableVerticalLayout.scss';

const dragBarStyle = {
  default: 'section-resize-handle-y',
  'cloudy-gray': 'section-resize-handle-y-cloudy-gray',
};

const ResizableVerticalLayout = ({
  initialSizes,
  minSizes,
  maxSizes,
  disableResize,
  children,
  forceHeight = false,
  dragBarVariant = 'default',
}) => {
  const [sizes, setSizes] = useState(initialSizes);
  const layoutId = useRef(uniqueId('resizable-vertical-layout-'));

  const themeClassname = dragBarVariant ? dragBarStyle[dragBarVariant] : 'default';

  // Update sizes state when initialSizes prop changes and is not equal to current sizes
  // This give ability to update sizes from parent component
  useEffect(() => {
    if (!isEqual(initialSizes, sizes)) {
      setSizes(initialSizes);
    }
  }, [initialSizes]);

  const getElementId = (index) => {
    return `${layoutId.current}-element-${index}`;
  };

  const onDragStop = (event, data, index) => {
    const elementId = getElementId(index);
    const nextElementId = getElementId(index + 1);
    const element = document.getElementById(elementId);
    const nextElement = document.getElementById(nextElementId);

    if (element && nextElement) {
      const newSizes = [...sizes];
      const size = sizes[index];
      const nextSize = sizes[index + 1];
      const elementHeight = isString(size) ? element.offsetHeight : size;
      const nextElementHeight = isString(nextSize) ? nextElement.offsetHeight : nextSize;
      newSizes[index] = elementHeight + data.y;
      newSizes[index + 1] = nextElementHeight - data.y;

      setSizes(newSizes);
    }
  };

  const lastIndex = children.length - 1;
  const elements = [];

  forEach(children, (child, index: number) => {
    const size = sizes[index];
    const elementId = getElementId(index);

    elements.push(
      React.cloneElement(child, {
        key: elementId,
        id: elementId,
        style:
          index < lastIndex
            ? {
                ...child.props.style,
                [forceHeight ? 'minHeight' : 'height']: size,
              }
            : { ...child.props.style, flexGrow: 1 },
      }),
    );

    if (index < lastIndex) {
      const nextElementId = getElementId(index + 1);
      const element = document.getElementById(elementId);
      const nextElement = document.getElementById(nextElementId);
      let minY = 0;
      let maxY = 0;

      if (element && nextElement) {
        const nextSize = sizes[index + 1];
        const minSize = minSizes[index];
        const nextMinSize = minSizes[index + 1];
        const maxSize = maxSizes ? maxSizes[index] : null;
        const elementHeight = isString(size) ? element.offsetHeight : size;
        const nextElementHeight = isString(nextSize) ? nextElement.offsetHeight : nextSize;
        minY = minSize - elementHeight;
        maxY = maxSize ? maxSize - elementHeight : nextElementHeight - nextMinSize;
      }

      elements.push(
        <div
          key={`resize-handle-${index}`}
          aria-label="resizable-cursor"
          style={{ position: 'relative' }}
        >
          {!disableResize && <div className={themeClassname} />}
          {/* @ts-ignore */}
          <Draggable
            axis="y"
            defaultClassName={!disableResize && themeClassname}
            defaultClassNameDragging={`${themeClassname}-active`}
            bounds={{ top: minY, bottom: maxY }}
            position={{ x: 0, y: 0 }}
            onStop={(event, data) => onDragStop(event, data, index)}
          >
            <div />
          </Draggable>
        </div>,
      );
    }
  });

  return (
    <div
      id={layoutId.current}
      className="resizable-vertical-layout"
      style={{ ...(disableResize ? { gap: 0 } : {}) }}
    >
      {elements}
    </div>
  );
};

export default ResizableVerticalLayout;
